import './style.scss';

import { useAuth0 } from '@auth0/auth0-react';
import {
  faArrowRightFromBracket,
  faChevronDown,
  faChevronUp,
  faGear,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  Button,
  CircularProgress,
  Container,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { useContext } from 'react';

import { restrictNavByPermission } from '../../config/feature-flags';
import AuthContext from '../../providers/auth/auth-service';
import { GlobalContext } from '../../providers/global-provider';
import { gray, primary } from '../../theme/colors';

interface Iprops {
  hasPermission?: boolean | true;
  isFullUserProfile: boolean;
}

export default function UserMenu(props: Iprops) {
  const { hasPermission, isFullUserProfile } = props;

  const { user, isLoading, logout } = useAuth0();
  const { iLogout } = useContext(AuthContext);
  const { setRenderSettings } = useContext(GlobalContext);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    iLogout();
    logout({ logoutParams: { returnTo: import.meta.env.REACT_APP_AUTH0_LOGOUT } });
  };

  if (isLoading) {
    return <CircularProgress size="1rem" />;
  }

  const styleObj = {
    borderRadius: 0,
    padding: 0,
    textAlign: 'left',
    '&:hover': {
      borderRadius: 0,
      color: gray[3],
      '> .user-menu-container': {
        borderColor: primary[3],
      },
    },
    '&:active': {
      borderRadius: 0,
      color: gray[3],
      '> .user-menu-container': {
        borderColor: primary[3],
      },
    },
  };

  const renderLogout = () => (
    <MenuItem onClick={() => handleLogout()}>
      <ListItemIcon>
        <FontAwesomeIcon icon={faArrowRightFromBracket} />
      </ListItemIcon>
      <ListItemText disableTypography>Logout</ListItemText>
    </MenuItem>
  );

  const renderAvatar = () => {
    return (
      <Avatar sx={{ width: '32px', height: '32px', fontSize: '16px' }}>
        {user ? (
          <img src={user.picture} width="32px" alt={user.name} />
        ) : (
          <FontAwesomeIcon icon={faUser} />
        )}
      </Avatar>
    );
  };

  const renderMobile = () => {
    return (
      <Button
        className="mobile-user-menu"
        sx={!isMobile ? { display: 'none' } : undefined}
        onClick={handleClick}
      >
        <Avatar sx={{ width: '32px', height: '32px', fontSize: '16px' }}>
          {user ? (
            <img src={user.picture} width="32px" alt={user.name} />
          ) : (
            <FontAwesomeIcon icon={faUser} />
          )}
        </Avatar>
      </Button>
    );
  };

  return (
    <div className="user-menu-wrapper">
      <Button
        onClick={handleClick}
        aria-controls={open ? 'menu' : undefined}
        aria-haspopup="true"
        disableRipple
        sx={isMobile ? { display: 'none' } : styleObj}
        aria-expanded={open ? 'true' : undefined}
        variant="text"
      >
        {!isFullUserProfile ? (
          renderAvatar()
        ) : (
          <Container className={`user-menu-container ${open && 'user-menu-container-open'}`}>
            <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ alignItems: 'center' }}>
              <Grid>{renderAvatar()}</Grid>
              <Grid>
                <Grid
                  container
                  direction="column"
                  rowSpacing={0}
                  columnSpacing={0}
                  sx={{ mx: '12px' }}
                >
                  <Grid>
                    <Typography className="user-name">{user?.nickname ?? user?.name}</Typography>
                  </Grid>
                  <Grid>
                    <Typography className="account-details">Account details</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid className="dropdownMenu">
                {open ? (
                  <FontAwesomeIcon icon={faChevronUp} />
                ) : (
                  <FontAwesomeIcon icon={faChevronDown} />
                )}
              </Grid>
            </Grid>
          </Container>
        )}
      </Button>
      {renderMobile()}
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ marginTop: '0.2rem' }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="menu-dropdown-container"
      >
        {/* IM-2595: restricted env (restrictNavByPermission ON) hides the
            settings / key-management entry entirely — UX only, the authoritative
            lock is server-side. OFF (production) → `!false`, so the gate is a
            no-op and Settings shows exactly as today. */}
        {user && hasPermission && !restrictNavByPermission && (
          <MenuItem
            onClick={() => {
              handleClose();
              setRenderSettings(true);
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faGear} />
            </ListItemIcon>
            <ListItemText disableTypography>Settings</ListItemText>
          </MenuItem>
        )}
        {renderLogout()}
      </Menu>
    </div>
  );
}
